// Centralized timezone constant - change this to support different timezones
// Common options: "Canada/Pacific", "America/New_York", "Europe/London", "Asia/Tokyo", etc.
export const APP_TIMEZONE = "Canada/Pacific";

const APP_DATE_ONLY_OPTIONS = {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

const APP_DATE_TIME_OPTIONS = {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const getCurrentDateInAppTimezone = () =>
  new Date().toLocaleDateString("en-CA", APP_DATE_ONLY_OPTIONS).replace(/\//g, "-");

export const currentDate = getCurrentDateInAppTimezone();

const createUtcDateFromAppDate = (value) => {
  const appDate = formatDateToYYYYMMDD_CA(value);
  if (!appDate) return null;

  const [year, month, day] = appDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

/**
 * Format a date value as YYYY-MM-DD in Canada/Pacific (APP_TIMEZONE).
 * Use for API params so dates are sent as date-only, CA-based, not UTC ISO.
 * @param {Date|string} value - Date object or YYYY-MM-DD string
 * @returns {string} YYYY-MM-DD in Canada/Pacific
 */
export const formatDateToYYYYMMDD_CA = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) return value;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', APP_DATE_ONLY_OPTIONS).replace(/\//g, '-');
};

export const shiftAppDate = (value, days = 0) => {
  const utcDate = createUtcDateFromAppDate(value ?? getCurrentDateInAppTimezone());
  if (!utcDate) return '';

  const offsetDays = Number(days);
  utcDate.setUTCDate(utcDate.getUTCDate() + (Number.isNaN(offsetDays) ? 0 : offsetDays));
  return utcDate.toISOString().slice(0, 10);
};

export const getDayDifferenceBetweenAppDates = (startDate, endDate, { inclusive = false } = {}) => {
  const start = createUtcDateFromAppDate(startDate);
  const end = createUtcDateFromAppDate(endDate);

  if (!start || !end) return null;

  const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return inclusive ? diffDays + 1 : diffDays;
};

/**
 * Last 7 days date range in APP_TIMEZONE (Canada/Pacific).
 * When enableFutureDates is false: Returns { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' } with endDate = today, startDate = 6 days before.
 * When enableFutureDates is true: Returns { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' } with startDate = today, endDate = today + 6 days (7 days total).
 */
export const getLast7DaysDateRange = (enableFutureDates) => {
  const today = getCurrentDateInAppTimezone();

  if (enableFutureDates) {
    return { startDate: today, endDate: shiftAppDate(today, 6) };
  }

  return { startDate: shiftAppDate(today, -6), endDate: today };
};

/**
 * Yesterday to today date range in APP_TIMEZONE (Canada/Pacific).
 * Returns { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' } with startDate = yesterday, endDate = today.
 */
export const getYesterdayToTodayDateRange = () => {
  const endDate = getCurrentDateInAppTimezone();
  return { startDate: shiftAppDate(endDate, -1), endDate };
};

export const getCurrentDateTimeInAppTimezone = () =>
  new Date()
    .toLocaleDateString("en-CA", APP_DATE_TIME_OPTIONS)
    .replace(/p\.m\./g, "PM")
    .replace(/a\.m\./g, "AM")
    .replace(/at/g, "");

export const getCurrentTimeInAppTimezone = () =>
  new Date().toLocaleTimeString("en-US", {
    timeZone: APP_TIMEZONE,
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
  });

export const currentDateTime = getCurrentDateTimeInAppTimezone();


export const initialPatientFormState = {
  phn: "",
  firstName: "",
  lastName: "",
  gender: "",
  phone: "",
  dob: "",
  email: "",
  route: "",
};

export const initalPatientAddressForm = {
  fullAddress: "",
  address: "",
  city: "",
  postalCode: "",
  province: "BC",
  country: "",
  mapUrl: "",
  lat: "",
  long: "",
};

export const intitalPatientFormErrors = {
  phn: "",
  firstName: "",
  lastName: "",
  route: "",
};

export const convertGenderValue = (value) => {
  switch (value) {
    case "M":
      return "Male";
    case "F":
      return "Female";
    default:
      return "";
  }
};

export const initalModals = {
  deletePatientModal: false,
  linkClinkModal: false,
  statusUpdateModal: false,
  patientNoteModal: false,
  doctorListModal: false,
  updateClinicModal: false,
  addDoctorModal: false,
  addClinicModal: false,
  viewAllCoverageLink: false,
  showPatientModle: false,
  showToast: false,
  deleteModal: false,
  showToastMessage: false,
  rxModal: false,
  // showPatientModle: false,
  addBlisterPackModal: false,
  updateBlisterlist: false,
  deleteBlisterModal: false,
  addNoteModal: false,
  printLabelModel: false,
  addBillingModal: false,
  deleteRevenueList: false,
  updated_logs: false,
  deleteBlisterPatient: false,
  deleteDailyVialsPatient: false,
  addPackOnShelfNotes: false
};

export const initalShowToastMessage = {
  clinicToast: false,
  clinicToastError: false,
  patientNoteToast: false,
  patientNoteToastError: false,
  showToast: false,
  errorToast: false,
  avrToast: false,
};

export const initalPatientNotes = {
  notes: "",
  recurrence: 0,
  selectedNoteOptions: "",
  remind: "",
  message: null,
};

export const initalAddDoctorForm = {
  lastName: "",
  firstName: "",
  cps_id: "",
};

export const initalAddDoctorFormError = {
  lastName: "",
  firstName: "",
  cps_id: "",
};

export const initalPharmacyInformationUpdateForm = {
  // date: currentDate,
  date: currentDate,
  fetchType: "winrx",
  phn: "",
  name: "",
  patientName: "",
  winrxPatientPhone: "",
  winrxPatientAddress: "",
  winrxPatientCity: "",
  winrxPatientProvince: "",
  winrxPatientPostcode: "",
  winrxPatientGender: '',
  winrxPatientDOB: '',
  cell_phone: '',
  Winrxphone: '',
  dob: "",
  note: "",
  clinicName: "",
  doctorName: "",
  clinicFaxNumber: "",
  pharmacyName: "",
  pharmacyManager: "",
  pharmacyFax: "",
  pharmacyPhone: "",
  pharmacyEmail: "",
  desgination: "Manager",
  toPharmacy: "",
  toPharmacyPhone: "",
  toPharmacyFax: "",
  toPharmacyAddress: "",
  toPharmacyManager: "",
  transferDate: currentDate,
  requested_Prescription: "",
  medicationLeftFor: "",
  medicationName: "",
  numberOfDaysSupplyRequested: "",
  planGFormRational: [2],
  planGFormProfession: "Nurse Practitioner",
  planZFormApplicationOption: "",
  planZFormRational: [],

  doctorCommnicationForm: "With Patient",
  doctorCommnicationFormType: "clinic",
  subjectOfFax: "",
  customToFaxNumber: "",
  customDoctor: "",
  customClinicName: "",
  textForFaxValue: "",

  // -----------Pharmacist Prescriber Communication.--------
  prescribingDateRange: "single_date",
  prescribingDate: currentDate,
  prescribingStartDate: currentDate,
  prescribingEndDate: currentDate,
  methadoneDoseDate: "",
  dailyDoseDate: "",
  prescriptionFolio: "",
  prescriptionCPID: "",
  descriptionAuthorized: "",
  returnForm: [],
  patientrecords: [],
  consumedDose: "",
  totalDose: "",
  canvaEditior: "",
  fromdate: "",
  file: "",
  todate: "",
  visitReason: ""
};

export const initalClinicFormData = {
  clinicName: "",
  streetName: "",
  phone: "",
  fax: "",
  city: "",
  province: "BC",
  zip: "",
  phoneExtension: "",
  lat: "",
  long: "",
  country: "",
  mapUrl: "",
  fullAddress: "",
};

export const initalMixingRxCheckEdItems = {
  Date: false,
  patient_name: false,
  drug_name_strength: false,
  quantity_to_dispense: false,
  frequency: false,
  dose_interval: false,
  max_daily_dose: false,
  quantity: false,
  interval_between_refills: false,
  printedname: false,
  uniquesignature: false,
};

export const initalTopHeaderAddNoteData = {
  patientName: "",
  phn: "",
  dob: "",
};

export const initalCompoundsFormData = {
  // date: currentDate,
  date: currentDate,
  toPharmacy: "",
  toPharmacyPhone: "",
  toPharmacyFax: "",
  toPharmacyManager: "",
  pharmacyName: "",
  pharmacyManager: "",
  desgination: "Manager",
  provider: "",
  compoundName: "",
  quantity: "",
  packageType: "",
  patientName: [],
  price: "",
  days: "",
  notes: "",
  rational: [],
  compound: "",
};

export const initalDispenseFormData = {
  // date: currentDate,
  date: currentDate,
  patientName: "",
  recurrence: false,
  rational: [],
  // patientName: "",
  route: "",
  driver: "",
  drugDetails: "",
  supervisorId: null,
  search: "",
  rxnumber: "",
};

export const initalAddNewDeliveryFormData = {
  name: "",
  email: "",
  phone: "",
  dob: "",
  password: "",
  passcode: "",
  image: "",
  supervisorId: null,
  default_pharmacist_id: "",
  is_supervisor: false,
  patientRoutes: ''
};


export const initalArchivedDelveriesData = {
  date: currentDate,
  startDate: currentDate,
  endDate: currentDate,
  deliveryStatus: null,
  patientRoutes: null,
  searchPatients: "",
  addNote: "",
  rxnumber: "",
  is_flexi: false,
  todayRx: false,
  summarize: false,
  comparisonday: null,
  facility: null,
  missing_patients: null,
  show_flag: null,
  reason: null,
  ack_filter: null,
  deliveryTimeFilter: '',
  deliveryTimeFrom: '',
  deliveryTimeTo: ''
};

export const initalDispenseLabelsFormData = {
  date: currentDate,
  patientRoutes: "",
  startDate: currentDate,
  endDate: shiftAppDate(currentDate, 4),
  rxnumber: "",
  search: "",
  muteValue: "",
};

export const initalExpiringRxFormData = {
  date: currentDate,
  patientRoutes: "",
  search: "",
  startDate: currentDate,
  endDate: shiftAppDate(currentDate, 1),
  rxType: "",
};

export const initalCounsellingData = {
  date: currentDate,
};

export const initialFormARVData = {
  date: currentDate,
  phn: "",
  winrxPatientPhone: "",
  name: "",
  fetchType: "winrx",
  pharmacyManager: "",
  patientName: "",
  patient: "",
  clinicName: "",
  drugName: "",
  duein: "",
  drugname: "",
  winrxPatientFirstName: "",
  winrxPatientLastName: "",
};

export const initialAVRFormErrors = {
  date: "",
  phn: '',
  name: '',
  winrxPatientPhone: '',

  pharmacyManager: '',
  patientName: '',
  clinicName: '',
}

export const initialARVDrugFormErrors = {
  drugName: '',
  dueInDays: ''
}

export const initalFdaFormData = {
  date: currentDate,
  fetchType: "winrx",
  phn: "",
  name: "",
  patientName: "",
  winrxPatientFirstName: "",
  winrxPatientLastName: "",
  winrxPatientPhone: "",
  pharmacyName: "",
  pharmacyManager: "",
  desgination: "Manager",
  rational: [],
  deductible: '',
  weekly: 'weekly',
  note: '',
  dob: "",
  doctorName: "",
  prescriber: "",
  initiatedDate: currentDate,
  other_name: "",
  doctDate: '',
  doctor: '',
  pharmacyfax: ''
}


export const initalSkinTestFormData = {
  date: currentDate,
  fetchType: 'winrx',
  phn: '',
  name: '',
  dob: '',
  phone: '',
  patientName: '',
  winrxpatientphn: '',
  winrxpatientname: '',
  winrxPatientPhone: '',
  winrxPatientAddress: '',
  winrxPatientProvince: '',
  winrxPatientPostcode: '',

  test_information_date: currentDate,
  site: '',
  lot_number: '',
  expiration_date: '',
  administered_by: 'Pharmacist',

  test_result_date: currentDate,
  induration: '',
  ppd_mantoux_test_result: '',

  interpreted_by: '',
  date_of_interpretation: currentDate,
  follow_up: '',
  comments: '',
  patient_consent: ''
}


export const initalPharmacySettingData = {
  winrx_pharmacy_id: '',
  winrx_status: '',
  winrx_token: '',

  fax_accound_id: '',
  fax_password: '',
  sender_fax_number: '',
  sender_email: '',
  fax_status: '',
  app_fcm_token_key: ''
}



export const initalAccountabilityReportData = {
  patientRoutes: "",
  startDate: shiftAppDate(currentDate, -1),
  endDate: shiftAppDate(currentDate, -1),
}
